/**
 * The new backend learns about a benefit set through Backend Sync, which is
 * normally sub-second but can lag up to about a minute. The delays add up to
 * 60 s so a write right after creating the set outlasts the worst case.
 */
export const SYNC_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 16000, 29000];

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Runs `attempt` once, then once more after each delay while `shouldRetry`
 * accepts the error. The last error is rethrown when the delays run out. */
export const retryWithBackoff = async <T>(
  attempt: () => Promise<T>,
  {
    delays,
    shouldRetry,
    sleep = wait
  }: {
    delays: number[];
    shouldRetry: (error: unknown) => boolean;
    sleep?: (ms: number) => Promise<void>;
  }
): Promise<T> => {
  for (const delay of delays) {
    try {
      return await attempt();
    } catch (error) {
      if (!shouldRetry(error)) throw error;
    }
    await sleep(delay);
  }

  return attempt();
};

/** Checked by shape so this module does not pull the API client into tests.
 * Only this code means "wait": a plain `not_found` (an unknown company) never
 * resolves by waiting. */
export const isNotSyncedYet = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { code?: unknown }).code === 'benefit_set_not_synced';

export const retryWhileNotSynced = <T>(
  attempt: () => Promise<T>,
  sleep?: (ms: number) => Promise<void>
): Promise<T> =>
  retryWithBackoff(attempt, { delays: SYNC_RETRY_DELAYS_MS, shouldRetry: isNotSyncedYet, sleep });
